import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, BookOpen } from "lucide-react"
import { motion } from "framer-motion"
import { useAuth } from "@/hooks/use-auth"
import { ChildAnalysisCard } from "@/components/dashboard/caregiver/ChildAnalysisCard"
import { Button } from "@/components/ui/button"
import { useCaregiverProfileData } from "@/hooks/use-caregiver-profile-data"

export function CaregiverProfile() {
  const { caregiverId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const {
    caregiver,
    selectedChild,
    selectedChildSleepData,
    isLoading,
    handleChildChange,
    children
  } = useCaregiverProfileData(caregiverId)

  // Get the timezone of the caregiver who inputted the data
  // This ensures dates are displayed in the original timezone they were created in
  const caregiverTimezone = caregiver?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

  const handleBack = () => {
    navigate('/coach-dashboard')
  }

  const handleAIAnalysisClick = () => {
    if (caregiver) {
      navigate(`/coach-dashboard/ai-analysis/${caregiver.caregiver_id}`);
    }
  }

  if (isLoading || !caregiver) {
    return <div>Loading...</div>
  }

  // Handle case when caregiver has no children
  if (!children || children.length === 0) {
    return (
      <div className="min-h-screen bg-[#141414] text-white overflow-x-hidden">
        <div className="container mx-auto px-4 py-6 lg:px-8">
          <motion.div 
            className="mb-6"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              onClick={handleBack}
              variant="outline"
              className="bg-transparent border-[#2A2A2A] hover:bg-[#2A2A2A] text-white transition-all duration-300 hover:scale-105"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </motion.div>

          <div className="bg-[#1C1C1C] rounded-xl p-8 text-center border border-[#2A2A2A]">
            <h2 className="text-2xl font-semibold mb-4">No Children Found</h2>
            <p className="text-gray-400">
              This caregiver doesn't have any children registered yet.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!selectedChild) {
    return <div>Loading...</div>
  }

  return (
    <div className="min-h-screen bg-[#141414] text-white overflow-x-hidden">
      <div className="container mx-auto px-4 py-6 lg:px-8">
        <motion.div 
          className="mb-6"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex justify-between items-center">
            <Button
              onClick={handleBack}
              variant="outline"
              className="bg-transparent border-[#2A2A2A] hover:bg-[#2A2A2A] text-white transition-all duration-300 hover:scale-105"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <Button
              variant="secondary"
              onClick={handleAIAnalysisClick}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              AI Analysis
            </Button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-8">
          {/* Analysis Content - Now Full Width */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="bg-[#1C1C1C] rounded-xl backdrop-blur-lg bg-opacity-50 border border-[#2A2A2A] shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-purple-500/30">
              <ChildAnalysisCard
                children={children || []}
                selectedChild={selectedChild}
                sleepLatency={{
                  latest: selectedChildSleepData?.latest || 'N/A',
                  average: selectedChildSleepData?.average || 'N/A'
                }}
                sleepRecords={selectedChildSleepData?.records || []}
                onChildChange={handleChildChange}
                timezone={caregiverTimezone}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
